import merge from 'lodash/merge';
import { allFeatureFlagsAccessed } from '@atlaskit/react-ufo/feature-flags-accessed';
import sendExperienceAnalyticsLegacy, {
	type AdditionalEventAttributes,
	type ExperienceDescription,
} from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import getMeta from '@atlassian/jira-get-meta';
import { fg } from '@atlassian/jira-feature-gating';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { AdditionalEventAttributes, ExperienceDescription };

const isSynthetic = () => getMeta('ajs-is-synthetic') === 'true';

const shouldAddConnectivityAttribute = (experienceDescription: ExperienceDescription) => {
	// Return false if feature flag is off
	if (!fg('thor_refine_taskfail_offline_failed_events')) return false;

	// Return false if not a taskFail action
	if (experienceDescription.wasExperienceSuccesful) return false;

	const isKnownExperience = [
		'transitionIssue',
		'getTransitions',
		'startWatching',
		'stopWatching',
	].includes(experienceDescription.experience);

	// Return false if not a known experience
	if (!isKnownExperience) return false;

	const hasStatusCode = !!experienceDescription.additionalAttributes?.statusCode;

	// Return false if status code is present
	return !hasStatusCode;
};
/**
 * sendExperienceAnalytics helper that is sampling events per user.
 *
 * @param {ExperienceDescription} experienceDescription - Description of the experience
 */
export function sendExperienceAnalytics(experienceDescription: ExperienceDescription): void {
	const additionalContext = {};

	const shard = getMeta('ajs-shard');
	const region = getMeta('ajs-region');
	Object.assign(additionalContext, { shard, region });

	const enabledFeatureFlags = Object.fromEntries(
		[...allFeatureFlagsAccessed].filter(([, value]) => value),
	);
	Object.assign(additionalContext, { enabledFeatureFlags });

	if (shouldAddConnectivityAttribute(experienceDescription)) {
		const isOnline =
			typeof globalThis.window !== 'undefined' && globalThis.window?.navigator?.onLine;
		Object.assign(additionalContext, { isOnline });
	}

	const synthetics = {
		additionalAttributes: {
			synthetic: isSynthetic(),
			...additionalContext,
		},
	};

	merge(experienceDescription, synthetics);
	sendExperienceAnalyticsLegacy(experienceDescription);
}
